import { Cast, IDataModel, Ignore, InitialValue, Model } from '@klickdata/core/application';
import {
    AfterModelInit,
    CastType,
    MethodType,
    ModelSync,
    Nullable,
    SortedArray,
} from '@klickdata/core/application/src/model/model-interface';
import { CustomField, CustomFieldData } from '@klickdata/core/custom-field';
import { CustomTab } from '@klickdata/core/custom-tab';
import { Customer } from '@klickdata/core/customer';
import { Group } from '@klickdata/core/group';
import { Media } from '@klickdata/core/media/src/media.model';
import { Notification, NotificationSetting } from '@klickdata/core/notification';
import { Resource, ResourceTagData } from '@klickdata/core/resource';
import { Downloads } from '@klickdata/core/resource/src/download.model';
import {
    AccessControlPermission,
    ApprovalMessage,
    Educator,
    Medias,
    ResourceApproval,
    ResourceData,
    Staff,
} from '@klickdata/core/resource/src/resource.model';
import { Filter, FilterModel, SelectFilterOption } from '@klickdata/core/table';
import { Task } from '@klickdata/core/task';
import { Utils } from '@klickdata/core/util';
import { PrivilegeType } from '@klickdata/core/util/src/type-util';
import { WidgetData } from '@klickdata/core/widget';
import * as moment from 'moment';
import { Observable } from 'rxjs';
import { Occasion } from './user-occasion/user-occasion.model';
import { UserRole } from './user-role/user-role.model';

export type RoleValueType =
    | 'guest'
    | 'staff'
    | 'user'
    | 'groupadmin'
    | 'divisionadmin'
    | 'customeradmin'
    | 'masteradmin'
    | 'superadmin';

export interface Answers {
    opportunity_id: number;
    answer: string;
}
export enum UserModelRoleValue {
    SUPERADMIN = 'superadmin',
    CUSTOMERADMIN = 'customeradmin',
    DIVISIONADMIN = 'divisionadmin',
    GROUPADMIN = 'groupadmin',
    INSTRUCTOR = 'instructor',
    USER = 'user',
    STAFF = 'staff',
    GUEST = 'guest',
}
export interface UserOccasion {
    started_at?: string;
    occasionStatus?: string;
    end_date?: string;
    id?: number;
    done?: string;
}

export interface UserData extends IDataModel {
    id?: number;
    title?: string;
    notes?: string;
    username?: string;
    fname?: string;
    lname?: string;
    name?: string;
    email?: string;
    avatar?: string;
    alias?: string;
    position?: string;
    disabled?: boolean;
    customer_id?: number;
    media_id?: number;
    media?: Media;
    customer?: Customer;
    role_value?: RoleValueType;
    end_date?: string;
    type?: string;
    verified?: boolean;
    activated?: boolean;
    created_by?: number;
    lang?: string;
    pref_lang_ids?: number[];
    employee_no?: string;
    use_manager_email?: boolean;
    manager_id?: string;
    phone?: string;
    created_at?: string;
    trial_ends_at?: string;
    updated_at?: string;
    deleted_at?: string;
    starting_date?: string;
    date_of_birth?: string;
    relation?: string;
    age_span?: string;
    gender?: string;
    opportunity_id?: number;
    permissions?: { [key: string]: string };

    sync_all_groups?: boolean;
    groups_attach?: number[];
    groups_detach?: number[];
    categories_attach?: number[];
    categories_detach?: number[];

    managed_groups?: number[];
    managed_divisions?: number[];
    cases_count?: number;
    author_cases_count?: number;
    last_seen?: string;
    about?: string;
    general_info?: string;
    references?: string[];
    section_ids?: number[];
    creator?: UserData;
    widgets?: WidgetData[];
    prompt_delimiters_enabled?: boolean;
    device_id?: string;

    /**
     * statistics
     */
    total_groups?: number;
    total_activities?: number;
    started_course_plan?: number;
    started_survey?: number;
    started_test?: number;
    started_course?: number;
    completed_course_plan?: number;
    completed_survey?: number;
    completed_test?: number;
    completed_course?: number;
    passed_test?: number;
    failed_test?: number;
    material_taken?: number;
    test_occasions?: number;
    course_occasions?: number;
    survey_occasions?: number;
    overall_time_spent?: number;
    learning_time_spent?: number;
    communications?: { [key: string]: any[] };

    /**
     * tools
     */
    submitText?: string;

    /**
     * User create - edit
     */
    groups?: {};
    group?: { [key: string]: any };
    resources?: ModelSync[];
    task?: any;
    staffRoleProperty?: string;
    newsletter?: boolean;
    staff?: Staff;
    medias?: Medias;
    priority?: string;
    tag_ids?: number[];
    custom_fields?: CustomField[];
    custom_tabs?: CustomTab[];
    message?: ApprovalMessage;
    resource_permissions?: AccessControlPermission[];
    notification_settings?: NotificationSetting[];
    group_id?: number;
    team_id?: number;
    recurring?: { resource_id?: number; metadata?: { recurring?: string; start_date?: string } };
    prompts_translation?: { [key: string]: string[] };
    metadata?: { [key: string]: any };
    main_editor?: boolean;
    idle_editor?: boolean;
    /** Form control path currently locked by this live editor; null when idle/unlocked. */
    dominated_field?: string | null;
}

export class User extends Model<UserData> implements FilterModel, AfterModelInit {
    id: number;
    username: string;
    fname: string;
    lname: string;
    name: string;
    email: string;
    avatar: string;
    alias: string;
    position: string;
    status: LoginStatus;
    disabled: boolean;
    customer_id: number;
    media_id: number;
    role_value: RoleValueType;

    @Cast(CastType.MOMENT)
    @Nullable(MethodType.PUT)
    end_date: moment.Moment;

    type: string;
    @Ignore()
    verified: boolean;
    @Ignore()
    is_customer_valid: boolean;
    @Ignore()
    activation_link: string;
    @Ignore()
    activated: boolean;
    @Ignore()
    created_by: number;
    lang: string; // siteLanguage
    pref_lang_ids: number[]; // Content languages
    employee_no: string;
    @Ignore()
    use_manager_email: boolean;
    manager_id: string;
    phone: string;
    title: string;
    notes: string;
    trial_ends_at: string;
    subscription_plan_id: number;
    @Cast(CastType.MOMENT)
    created_at: moment.Moment;
    @Cast(CastType.MOMENT)
    updated_at: moment.Moment;
    @Cast(CastType.MOMENT)
    deleted_at: moment.Moment;
    @Cast(CastType.MOMENT)
    date_of_birth: moment.Moment;
    @Cast(CastType.MOMENT)
    starting_date: moment.Moment;
    opportunity_id: number;
    total_groups: number;
    old_password: string;
    new_password: string;
    permissions: { [key: string]: string };
    test_occasions: number;
    course_occasions: number;
    survey_occasions: number;
    @InitialValue([])
    groups_attach: number[];
    @InitialValue([])
    groups_detach: number[];
    @InitialValue([])
    categories_attach: number[];
    @InitialValue([])
    categories_detach: number[];
    @InitialValue([])
    @SortedArray()
    references: string[];
    overall_time_spent: number;
    learning_time_spent: number;
    cases_count: number;
    author_cases_count: number;
    @Cast(CastType.MOMENT)
    last_seen: moment.Moment;
    about: string;
    general_info: string;
    age_span: string;
    gender: string;
    @InitialValue([])
    section_ids: number[];
    @Ignore()
    checked: boolean;
    relation: string;
    @Ignore()
    creator: UserData;
    widgets: WidgetData[];
    prompt_delimiters_enabled: boolean;
    @Ignore()
    occasions: Occasion[];
    @Ignore()
    groups$: Observable<Group[]>;
    @Ignore()
    answers: Answers[];
    @Ignore()
    main_editor: boolean;
    @Ignore()
    idle_editor: boolean;
    @Ignore()
    dominated_field: string | null;
    @Cast(CastType.OBJECT)
    communications: { [key: string]: any[] };
    @Cast(CastType.CLOSURE, (value: CustomFieldData[], old: CustomFieldData[]) => {
        const oldFields = new Map(old?.map((f) => [f.id, f.value]));
        const updates = value
            .filter((field) => field.value !== oldFields?.get(field.id))
            .map((f) => ({ id: f.id, value: f.value }));

        return updates.length ? updates : undefined;
    })
    custom_fields: CustomField[];
    @Cast(CastType.CLOSURE, Utils.modelSync)
    custom_tabs: CustomTab[];
    @Cast(CastType.OBJECT)
    address: {};
    tag_ids: number[];
    newsletter: boolean;
    @Ignore()
    customer: any;
    priority: string;
    @Ignore()
    managers: any;
    @Ignore()
    last_login: string;
    @Ignore()
    device_id: string;
    occasion: UserOccasion;
    @Ignore()
    activations: any[];
    @Cast(CastType.OBJECT)
    metadata: { [key: string]: any };
    /**
     * User create - edit
     */
    @Cast(CastType.CLOSURE, Utils.modelSync)
    groups?: ModelSync;
    @Cast(CastType.CLOSURE, Utils.modelSync)
    sections?: ModelSync;
    @Cast(CastType.CLOSURE, Utils.modelSync)
    resources?: ModelSync[];
    prompts_translation: { [key: string]: string[] };
    @Ignore()
    public downloads?: Downloads;
    task: Task;
    @Ignore()
    loading: boolean;
    @Ignore()
    tags: ResourceTagData[];
    @Ignore()
    staffRoleProperty: string;
    @Ignore()
    privileges: Privileges;

    @Cast(CastType.CLOSURE, Utils.filterUpdatedObject)
    public medias: Medias;
    @Cast(CastType.CLOSURE, Utils.filterUpdatedObject)
    public staff: Staff;
    createdBy: Observable<User>;
    resource_permissions: AccessControlPermission[];
    notification_settings: NotificationSetting[];
    @Ignore()
    actions?: { [key: string]: any };
    @Ignore()
    all_resource_perm_checked: boolean;
    @Ignore()
    some_resource_perm_checked: boolean;
    @Nullable(MethodType.PUT)
    group_id: number;
    @Nullable(MethodType.PUT)
    team_id: number;

    @Cast(CastType.CLOSURE, Utils.scopedModelSync)
    competences?: ResourceData[];
    @Ignore()
    exercises?: ResourceData[];
    @Ignore()
    group: { id: number; name: string };
    @Ignore()
    enrollment?: ResourceApproval;
    @Ignore()
    signoff?: any;
    @Ignore()
    can_practice?: boolean;
    @Ignore()
    competenceIds: number[];

    afterModelInit(data: UserData): void {
        if (!data.name) {
            this.name = this.fname && this.lname ? `${this.fname} ${this.lname}` : this.fname || this.lname || null;
        }

        if (this.last_seen) {
            if (moment().diff(this.last_seen, 'minutes') <= 5) {
                this.status = LoginStatus.Online;
            } else if (moment().diff(this.last_seen, 'days') > 30) {
                this.status = LoginStatus.OfflineMoreThan30Days;
            } else {
                this.status = LoginStatus.Offline;
            }
        }

        if (this.competences)
            this.competenceIds = this.competences
                .filter((res: ResourceData) => !!res.occasion_id)
                .map((res: ResourceData) => res.occasion_id);
    }

    /** Whether this editor should appear on the resource information page. */
    get showsOnInfoPage(): boolean {
        const show = this.metadata?.['show'];
        return show === true || show === 1 || show === '1' || show === 'true';
    }

    get dashboardTabs(): CustomTab[] {
        return this.custom_tabs?.filter(
            (tab) =>
                !tab.metadata?.disabled &&
                ((tab.scope_id === 1 && !!tab.available_scopes?.length) ||
                    (tab.scope_id === 9 && !!tab.available_sections?.length))
        );
    }
    get canPractice(): boolean {
        if (!this.custom_tabs) return false;
        return !!this.custom_tabs.find(
            (tab) =>
                !!tab.metadata?.filters?.length &&
                !!tab.metadata?.filters?.find(
                    (filter) => filter.property === 'section_activity_base' && filter.items.includes('practiceForUser')
                )
        );
    }
    get hasActivities(): boolean {
        return !!this.dashboardTabs?.length;
    }

    get finalResult(): Occasion {
        return Array.isArray(this.occasions) ? this.occasions[0] : this.occasions;
    }
    get activeOrderedCustomFields(): CustomField[] {
        return this.custom_fields?.sort((a, b) => a.order - b.order)?.filter((item) => !item.disabled) ?? [];
    }
    /***
     *  enrollment: {approved: 1,rejected: 1,pending: 0},
     *  review: {approved: 1,rejected: 0,pending: 1}
     */
    get hasActions(): boolean {
        return this.hasEnrollments || this.hasReviews || this.hasAssignments;
    }

    get hasEnrollments(): boolean {
        const enrollment = this.actions?.enrollment;
        if (!enrollment) return false;
        return enrollment.enroll_required > 0 || this.totalEnrollments > 0;
    }
    get hasWaitingList(): boolean {
        return !!this.actions?.enrollment?.waiting && this.actions?.enrollment?.waiting > 0;
    }

    get hasReviews(): boolean {
        return this.totalReviews > 0;
    }

    get hasAssignments(): boolean {
        return this.totalAssignments > 0;
    }

    get pendingActions(): number {
        return this.pendingEnrollments + this.pendingReviews + this.pendingAssignments;
    }

    get pendingEnrollments(): number {
        const enrollment = this.actions?.enrollment;
        if (!enrollment) return 0;
        return enrollment.pending || 0;
    }

    get totalEnrollments(): number {
        const enrollment = this.actions?.enrollment;
        if (!enrollment) return 0;
        return (
            enrollment.approved +
            enrollment.enrolled +
            enrollment.rejected +
            enrollment.pending +
            enrollment.waiting +
            enrollment.overdue
        );
    }

    get totalReviews(): number {
        const review = this.actions?.review;
        if (!review) return 0;
        return review.approved + review.rejected + review.pending + review.waiting + review.overdue;
    }
    get totalAssignments(): number {
        const assignments = this.actions?.assignments;
        if (!assignments) return 0;
        return (
            assignments.assigned +
            assignments.rejected +
            assignments.pending +
            assignments.waiting +
            assignments.overdue
        );
    }

    get pendingReviews(): number {
        const review = this.actions?.review;
        if (!review) return 0;
        return review.pending || 0;
    }
    get pendingAssignments(): number {
        const assignments = this.actions?.assignments;
        if (!assignments) return 0;
        return assignments.pending || 0;
    }

    get userAndTaskTags(): ResourceTagData[] {
        const userTags = this.tags ?? [];
        const taskTags = this.task?.tags ?? [];

        return [...userTags, ...(<ResourceTagData[]>taskTags)];
    }

    get signature(): string {
        const name =
            this.name ?? (this.fname && this.lname ? `${this.fname} ${this.lname}` : this.fname || this.lname || null);
        return name
            ?.match(/\b(\w)/g)
            ?.join('')
            .toUpperCase();
    }

    get finalAnswer(): Answers {
        return this.answers[0];
    }

    get isTrialEnded(): boolean {
        if (this.trial_ends_at) {
            return moment().isSameOrAfter(this.trial_ends_at);
        }
        return false;
    }

    get userAsEducator(): Educator {
        return <Educator>{
            name: this.name,
            id: this.id,
            email: this.email,
            phone: this.phone,
            about: this.about,
            title: this.title,
            media_id: this.media_id,
            media: this.media,
            task: this.task,
            communications: this.communications,
        };
    }

    get userRoleOptions(): SelectFilterOption[] {
        return [
            { title: $localize`Main Admin`, value: 'customerAdmin', icon: 'groups' },
            { title: $localize`Group Admin`, value: 'groupAdmin', icon: 'group' },
            { title: $localize`User`, value: 'user', icon: 'person' },
        ];
    }

    get userStatusOptions(): SelectFilterOption[] {
        return [
            { title: $localize`:@@active:Active`, value: 'active', icon: 'notifications_active' },
            { title: $localize`Inactive (30d)`, value: 'inactive', icon: 'verified_user' },
            { title: $localize`Unactivated`, value: 'unactivated', icon: 'sync_disabled' },
            { title: $localize`Deleted`, value: 'deleted', icon: 'auto_delete' },
            { title: $localize`Expired`, value: 'expired', icon: 'event_busy' },
            { title: $localize`Imported`, value: 'imported', icon: 'label_important' },
        ];
    }

    managed_groups: number[];
    managed_divisions: number[];
    role: Observable<UserRole>;

    /**
     * Need extra validation when we will handle resource editing privilege.
     * And differentiate between each user role according
     * a. Resource author.
     * b. Resource customer. connected with a.
     * c. Resource public
     */
    public canEdit(resource: { customer_id: number; author_id?: number; disabled?: boolean }): boolean {
        return !resource.disabled && (this.isOwner(resource) || this.hasPrivilege(resource));
    }

    public canEditMedia(media: { created_by?: number; author_id?: number }): boolean {
        return this.isMasterAdmin() || this.isOwner(media);
    }
    public canViewMediaLink(media: { created_by?: number; author_id?: number; visibility?: string }): boolean {
        return this.canEditMedia(media) || media.visibility === 'public';
    }

    public canEditFromCollaboration(resource: { user_collaboration: any }): boolean {
        return resource.user_collaboration?.edit?.status;
    }

    public canDownload(resource: { downloads: Downloads }): boolean {
        return !!resource.downloads;
    }
    get nextContactTask(): Task {
        return this.task?.action === 'next_contact' ? this.task : null;
    }
    // public canDownloadPDF(resource: Resource): boolean {
    //     // return (
    //     //     resource.occasionStatus === 'done' ||
    //     //     (resource.occasionStatus !== 'done' && this.role_value !== 'guest') ||
    //     //     (resource.occasionStatus !== 'done' && this.role_value !== 'user')
    //     // );
    //     // Only courses can be downloaded
    //     return (
    //         resource.downloads &&
    //         (ResourceTypes.parentType(resource.type_id) === ResourceTypes.COURSE ||
    //             resource.scope_id === AppScope.COURSE)
    //     );
    // }
    public canEditMultiple(users: { customer_id: number; author_id?: number }[]): boolean {
        return !users.find((user) => !this.canEdit(user));
    }

    public canEmail(resource: { customer_id: number; author_id?: number }): boolean {
        return this.canEdit(resource);
    }

    public hasViewPrivilege(resource: ResourceData): boolean {
        return (
            this.role_value === 'superadmin' ||
            this.isOwner(resource) ||
            resource.public ||
            resource.customer_id === this.customer_id
        );
    }

    public canPublish(resource: {
        customer_id: number;
        author_id?: number;
        ready_publish?: boolean;
        published: string;
        last_publish?: string;
    }): boolean {
        return !resource.last_publish && this.hasPublishPrivilege(resource);
    }

    // public canPublishResource(): boolean {
    //     return this.privileges?.publish === 'ALLOWED_PUBLISH';
    // }
    // public canRequestPublishResource(): boolean {
    //     return this.privileges?.publish === 'REQUEST_PUBLISH';
    // }

    // public canPublicResource(resourceData: { last_publish: boolean }): boolean {
    //     return resourceData.last_publish && this.privileges?.public === 'ALLOWED_PUBLISH';
    // }

    public canUnPublished(resource: {
        customer_id: number;
        author_id?: number;
        published: string;
        ready_publish?: boolean;
        last_publish?: string;
    }): boolean {
        return (resource.published || resource.last_publish) && this.hasPublishPrivilege(resource);
    }

    public hasPublishPrivilege(resource: {
        customer_id: number;
        ready_publish?: boolean;
        author_id?: number;
    }): boolean {
        return (
            (resource.ready_publish == null || resource.ready_publish) &&
            (this.hasPrivilege(resource) || (this.isOwner(resource) && !!this.permissions?.unpublished))
        );
    }

    public canAssign(resource: Resource): boolean {
        return (
            resource.isPublished() &&
            resource.isAvailable() &&
            (this.hasPrivilege(resource) || this.isOwner(resource) || resource.public)
        );
    }
    public canCollect(resource: Resource): boolean {
        return this.canAssign(resource);
    }
    public canMakeMandatory(resource: Resource): boolean {
        return this.hasPrivilege(resource);
    }
    public canSetAccessControls(resource: Resource): boolean {
        return this.canAssign(resource);
    }
    public canRecommend(resource: Resource): boolean {
        return this.canAssign(resource);
    }
    public isMessageAuthor(message: Notification): boolean {
        return message.author?.id === this.id;
    }

    public canBrowse(resource: { customer_id: number; author_id?: number; published: string }): boolean {
        return !!resource.published;
    }

    public canPublicize(resource: {
        customer_id: number;
        author_id?: number;
        public: boolean;
        published: boolean;
    }): boolean {
        return (
            !resource.public &&
            !!resource.published &&
            (this.hasPrivilege(resource) || (this.isOwner(resource) && !!this.permissions?.publicize))
        );
    }

    public canUnPublicized(resource: { customer_id: number; author_id?: number; public: boolean }): boolean {
        return (
            resource.public &&
            (this.hasPrivilege(resource) || (this.isOwner(resource) && !!this.permissions?.unpublicized))
        );
    }

    /**
     * Check if user is the owner of resource if resoure has ownership author_id property
     */
    private isOwner(model: { author_id?: number; created_by?: number; user_id?: number }): boolean {
        return (
            this.role_value !== 'guest' &&
            ((model instanceof User && model.id === this.id) || // user can edit his profile.
                model.author_id === this.id ||
                model.created_by === this.id ||
                model.user_id === this.id)
        );
    }

    public canCheckOcc(occUser: User): boolean {
        return !!occUser && (this.id == occUser.id || this.hasPrivilege(occUser));
    }

    private hasPrivilege(resource: { customer_id: number }): boolean {
        return (
            this.role_value === 'superadmin' ||
            (resource.customer_id === this.customer_id &&
                (this.isAdmin() || (resource instanceof User && resource.role_value === 'user')))
        );
    }

    public canPlayAsGroupLeader(): boolean {
        return this.isAdmin() && this.metadata?.isPlayAsGroupLeader;
    }

    public isDivisionAdmin(): boolean {
        return this.isOneOfRoles([UserModelRoleValue.DIVISIONADMIN]);
    }
    public isGroupAdmin(): boolean {
        return this.isOneOfRoles([UserModelRoleValue.GROUPADMIN]);
    }
    public isAdmin(): boolean {
        return !this.isOneOfRoles([UserModelRoleValue.USER, UserModelRoleValue.GUEST]);
    }
    public isMasterAdmin(): boolean {
        return this.isOneOfRoles([UserModelRoleValue.SUPERADMIN]);
    }

    public isOneOfRoles(roles: UserModelRoleValue[]): boolean {
        return roles.includes(this.role_value as UserModelRoleValue);
    }

    public hasRoleHigherThan(role: string, specficRole?: string): boolean {
        return this.allUserRoles.indexOf(specficRole ?? this.role_value) < this.allUserRoles.indexOf(role);
    }

    /**
     * Limited recording duration in ms when user is not admin
     * and his academy didn't pay for him (last condition not applied until EB clarify)
     */
    public canRecordUnlimited(): boolean {
        return this.role_value !== 'user' && this.role_value !== 'guest';
    }

    public hasQuickAccess(): boolean {
        return Utils.checkHasQuickAccess(this);
    }

    public preventChangeLibraryResStatus(isLibraryRes: boolean): boolean {
        return !this.isMasterAdmin() && isLibraryRes;
    }

    public canDelete(resource: { id: number; customer_id: number; author_id?: number; default?: boolean }): boolean {
        return !resource.default && !this.isMe(resource) && this.canEdit(resource); // user can't delete his profile.
    }

    public canDeleteMultiple(users: any): boolean {
        return !users.find((user) => !this.canDelete(user));
    }

    private isMe(resource: { id: number }): boolean {
        return resource instanceof User && resource.id === this.id;
    }

    public canSendActivation(user: User): boolean {
        return this.canEdit(user) && !user.activated;
    }
    public getActivationMailInfo(user: User): { class: string; tooltip: string } {
        if (!user.is_customer_valid) {
            return { class: 'greycolor', tooltip: $localize`Academy is not Ready yet` };
        }
        if (this.isUserExpired(user)) {
            return { class: 'redcolor', tooltip: $localize`Expired` };
        }

        const latestActivation = user.activations?.[user.activations.length - 1];
        const sender = latestActivation?.author.name || null;
        const sentAt = latestActivation?.created_at
            ? moment(latestActivation.created_at).format('YYYY-MM-DD HH:mm')
            : null;
        const tooltip = this.getActivationTooltip(sender, sentAt, user.activated);
        return {
            class: user.activated ? 'greenColor' : sender && sentAt ? 'orangeColor' : 'darkBluecolor',
            tooltip,
        };
    }

    private getActivationTooltip(sender: string | null, sentAt: any, isActivated: boolean): string {
        if (isActivated) {
            return sender && sentAt
                ? $localize`Activation sent on ${sentAt} by ${sender} and activated`
                : $localize`Activated`;
        } else {
            return sender && sentAt
                ? $localize`Activation sent on ${sentAt} by ${sender} and not activated yet`
                : $localize`No activation mail sent yet`;
        }
    }

    private isUserExpired(user: User): boolean {
        return user.end_date && user.end_date.isBefore(moment());
    }

    get roleValueSpecs(): { [key: string]: string } {
        // const roles = {
        //     superadmin: { icon: 'admin_panel_settings', title: $localize`Master Admin`, color: '#ff9961' },
        //     customeradmin: { icon: 'groups', title: $localize`Main Admin`, color: '#3e5365' },
        //     divisionadmin: { icon: 'diversity_2', title: $localize`Division Admin`, color: '#3e5365' },
        //     groupadmin: { icon: 'group', title: $localize`Group Admin`, color: '#bfd8d0' },
        //     staff: { icon: 'person_4', title: $localize`Contact`, color: '#dfdfdf' },
        //     instructor: { icon: 'engineering', title: $localize`Instructor`, color: '#dfdfdf' },
        //     user: { icon: 'person', title: $localize`Learner`, color: '#dfdfdf' },
        //     default: { icon: 'person', title: $localize`Learner`, color: '#dfdfdf' },
        // };
        const roles = this.allUserRolesSpecs().reduce((acc, role) => {
            acc[role.value] = {
                icon: role.icon,
                title: role.title,
                color: role.color,
            };
            return acc;
        }, {});
        return roles[this.role_value] || roles['default'];
    }
    public allUserRolesSpecs(): { value: string; icon: string; title: string; color: string }[] {
        return [
            { value: 'superadmin', icon: 'admin_panel_settings', title: $localize`Master Admin`, color: '#ff9961' },
            { value: 'customeradmin', icon: 'groups', title: $localize`Main Admin`, color: '#3e5365' },
            { value: 'divisionadmin', icon: 'diversity_2', title: $localize`Division Admin`, color: '#3e5365' },
            { value: 'groupadmin', icon: 'group', title: $localize`Group Admin`, color: '#bfd8d0' },
            { value: 'instructor', icon: 'engineering', title: $localize`Instructor`, color: '#dfdfdf' },
            { value: 'user', icon: 'person', title: $localize`Learner`, color: '#dfdfdf' },
            { value: 'staff', icon: 'person_4', title: $localize`Contact`, color: '#dfdfdf' },
            { value: 'default', icon: 'person', title: $localize`Learner`, color: '#dfdfdf' },
        ];
    }

    get allUserRoles(): string[] {
        return ['superadmin', 'customeradmin', 'divisionadmin', 'groupadmin', 'instructor', 'user', 'staff', 'guest'];
    }
    public canOverwriteRole(authRole: string, role: string) {
        return this.allUserRoles.indexOf(authRole) <= this.allUserRoles.indexOf(role);
    }

    get userPermissionLevels(): string[] {
        return ['MASTER', 'CLUSTER', 'PUBLIC', 'CUSTOMER', 'GROUP', 'USER'];
    }
    public getFilter(): Filter<string | number> {
        return {
            property: this.staffRoleProperty,
            items: [this.id],
            chips: [{ id: this.id, label: this.name, selected: true }],
        };
    }
}
export interface Privileges {
    public?: PrivilegeType;
    publish?: PrivilegeType;
    create?: PrivilegeType;
}

/**
 * Enum representing possible login status values
 */
export enum LoginStatus {
    Offline = 0,
    Online = 1,
    OfflineMoreThan30Days = -1,
}

/**
 * Interface for the login status return type
 */
export interface LoginStatusInfo {
    tooltip: string;
    styleClass: string;
}
